import React, { useEffect, useState } from 'react';
import { LootboxReward } from './RitualContext';
import { X, Sparkles, Gift, Diamond, Clock, Image, Play, Crown, Share2, LockIcon, UnlockIcon, Star, Flame, ChevronUp, Plus } from 'lucide-react';
interface LootboxOpeningProps {
  reward: LootboxReward | null;
  showReward: boolean;
  onClose: () => void;
}
// Mystery bonus reward types
type MysteryBonus = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
};
export const LootboxOpening: React.FC<LootboxOpeningProps> = ({
  reward,
  showReward,
  onClose
}) => {
  const [isShaking, setIsShaking] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showLockBreak, setShowLockBreak] = useState(false);
  const [showMysteryCards, setShowMysteryCards] = useState(false);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [revealedBonus, setRevealedBonus] = useState<MysteryBonus | null>(null);
  const [showShareOption, setShowShareOption] = useState(false);
  const [sparkCount, setSparkCount] = useState<number[]>([]);
  // Mystery bonus options
  const mysteryBonuses: MysteryBonus[] = [{
    id: 'double-gems',
    title: 'Double Gems Boost',
    description: 'All gem rewards doubled for the next 24 hours!',
    icon: <Diamond size={24} className="text-blue-400" data-id="element-17381" />,
    color: 'from-blue-400 to-cyan-400'
  }, {
    id: 'exclusive-skin',
    title: 'Exclusive Lingerie Skin',
    description: 'Unlock a limited-edition lingerie skin for any Angel!',
    icon: <Sparkles size={24} className="text-pink-400" data-id="element-17382" />,
    color: 'from-pink-400 to-purple-400'
  }, {
    id: 'xp-boost',
    title: 'XP Boost',
    description: 'Earn +50% XP from all activities for 3 days!',
    icon: <Flame size={24} className="text-amber-400" data-id="element-17383" />,
    color: 'from-amber-400 to-orange-400'
  }];
  useEffect(() => {
    // Generate random sparks for animation
    setSparkCount(Array.from({
      length: 15
    }, (_, i) => i));
    // Unlock animation sequence
    const unlockTimer = setTimeout(() => {
      setIsUnlocking(true);
      setTimeout(() => {
        setShowLockBreak(true);
        setTimeout(() => {
          // Start shaking animation
          setIsShaking(true);
          // After shaking, start opening animation
          const openTimer = setTimeout(() => {
            setIsShaking(false);
            setIsOpening(true);
            // After opening animation, show mystery cards if reward is shown
            if (showReward) {
              const mysteryTimer = setTimeout(() => {
                setShowMysteryCards(true);
              }, 1000);
              return () => clearTimeout(mysteryTimer);
            }
          }, 1500);
          return () => clearTimeout(openTimer);
        }, 800);
      }, 1200);
    }, 500);
    return () => clearTimeout(unlockTimer);
  }, [showReward]);
  // Handle card selection
  const handleCardSelect = (index: number) => {
    setSelectedCard(index);
    // Randomly select a mystery bonus
    const randomBonus = mysteryBonuses[Math.floor(Math.random() * mysteryBonuses.length)];
    setTimeout(() => {
      setRevealedBonus(randomBonus);
      setTimeout(() => {
        setShowShareOption(true);
      }, 1000);
    }, 800);
  };
  // Handle sharing reward
  const handleShareReward = () => {
    // Implement share functionality - for now just close the modal
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300" data-id="element-17384">
      <div className="relative max-w-md w-full rounded-2xl overflow-hidden" data-id="element-17385">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white" onClick={onClose} data-id="element-17386">
          <X size={18} data-id="element-17387" />
        </button>
        {!showReward || !showMysteryCards && showReward ? <div className="bg-gradient-to-b from-purple-900/80 via-indigo-900/80 to-black p-8 rounded-2xl shadow-2xl flex flex-col items-center relative overflow-hidden" data-id="element-17388">
            {/* Animated background effects */}
            <div className="absolute inset-0 overflow-hidden" data-id="element-17389">
              <div className="absolute inset-0 bg-[url('https://assets.codepen.io/13471/sparkles.gif')] opacity-20 bg-cover mix-blend-screen" data-id="element-17390"></div>
              {/* Random sparks */}
              {sparkCount.map(i => <div key={i} className="absolute w-1 h-1 bg-yellow-300 rounded-full animate-float-spark" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 7}s`
          }} data-id="element-17391"></div>)}
            </div>
            <div className="relative z-10" data-id="element-17392">
              <h2 className="text-white text-xl font-bold mb-2 text-center" data-id="element-17393">
                Weekly Premium Lootbox
              </h2>
              <p className="text-amber-300 text-sm mb-6 text-center font-medium" data-id="element-17394">
                {isUnlocking ? '🔥 Congratulations! Your Premium Ritual has unlocked the Weekly Lootbox!' : "Complete your Premium Ritual to unlock this week's epic lootbox."}
              </p>
            </div>
            <div className="relative mb-6" data-id="element-17395">
              {/* Lootbox container with glow effect */}
              <div className={`relative ${isShaking ? 'animate-shake' : ''} ${isOpening ? 'animate-open-box' : ''} transition-all duration-700`} data-id="element-17396">
                <div className="w-48 h-48 relative" data-id="element-17397">
                  {/* Outer glow effect */}
                  <div className={`absolute inset-0 rounded-xl blur-md transition-all duration-1000 ${isUnlocking ? 'bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 opacity-70 animate-pulse-glow' : 'bg-gradient-to-r from-blue-500 to-purple-500 opacity-30'}`} data-id="element-17398"></div>
                  {/* The chest */}
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-17399">
                    <div className={`w-36 h-36 ${isUnlocking ? 'bg-gradient-to-b from-yellow-500 to-amber-700' : 'bg-gradient-to-b from-indigo-700 to-purple-900'} rounded-xl shadow-2xl flex items-center justify-center transition-all duration-700 relative overflow-hidden`} data-id="element-17400">
                      {/* Chest texture overlay */}
                      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-30" data-id="element-17401"></div>
                      {/* Chest lid highlight */}
                      <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/20 to-transparent" data-id="element-17402"></div>
                      {/* Chest lock */}
                      {!isOpening && <div className="absolute inset-0 flex items-center justify-center" data-id="element-17403">
                          <div className={`relative transition-all duration-500 ${showLockBreak ? 'scale-150 opacity-0' : 'scale-100 opacity-100'}`} data-id="element-17404">
                            {isUnlocking ? <div className="relative" data-id="element-17405">
                                <UnlockIcon size={48} className="text-yellow-300 animate-pulse" data-id="element-17406" />
                                {showLockBreak && <div className="absolute inset-0 flex items-center justify-center" data-id="element-17407">
                                    <span className="text-3xl animate-pop" data-id="element-17408">
                                      💥
                                    </span>
                                  </div>}
                              </div> : <LockIcon size={48} className="text-gray-400" data-id="element-17409" />}
                          </div>
                        </div>}
                      {/* Gift icon */}
                      <Gift size={isOpening ? 64 : 32} className={`${isOpening ? 'text-yellow-300 animate-bounce-small' : 'text-gray-400'} transition-all duration-500`} data-id="element-17410" />
                    </div>
                  </div>
                  {/* Animated particles effect */}
                  {(isUnlocking || isOpening) && <div className="absolute inset-0 pointer-events-none" data-id="element-17411">
                      {sparkCount.slice(0, 8).map(i => <div key={`spark-${i}`} className={`absolute w-2 h-2 rounded-full ${isOpening ? 'animate-spark-fly' : 'animate-spark-float'}`} style={{
                  backgroundColor: ['#f59e0b', '#fcd34d', '#f97316', '#fef3c7'][Math.floor(Math.random() * 4)],
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  transform: `scale(${0.5 + Math.random() * 1.5})`
                }} data-id="element-17412"></div>)}
                    </div>}
                </div>
              </div>
              {/* Shine effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/5 to-white/0 pointer-events-none" data-id="element-17413"></div>
            </div>
            <p className="text-white/80 text-sm mt-2 text-center relative z-10" data-id="element-17414">
              {isShaking ? 'Your lootbox is bursting with excitement!' : isOpening ? 'Revealing your premium rewards...' : isUnlocking ? 'Unlocking your treasure...' : 'Premium purchase required to unlock'}
            </p>
          </div> : showMysteryCards ? <div className="bg-gradient-to-b from-purple-900/80 via-indigo-900/80 to-black p-8 rounded-2xl shadow-2xl" data-id="element-17415">
            <div className="flex flex-col items-center" data-id="element-17416">
              <h2 className="text-white text-xl font-bold mb-2" data-id="element-17417">
                {selectedCard !== null ? 'Mystery Bonus Revealed!' : 'Choose Your Mystery Bonus'}
              </h2>
              <p className="text-amber-300 text-sm mb-6 text-center" data-id="element-17418">
                {selectedCard !== null ? 'You unlocked an exclusive bonus reward!' : 'Tap a card to reveal your mystery bonus'}
              </p>
              {selectedCard === null ? <div className="grid grid-cols-3 gap-4 w-full max-w-xs mb-6" data-id="element-17419">
                  {[0, 1, 2].map(index => <div key={index} className="relative cursor-pointer transform transition-all duration-300 hover:scale-105" onClick={() => handleCardSelect(index)} data-id="element-17420">
                      {/* Card back */}
                      <div className="aspect-[2/3] bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-xl flex items-center justify-center p-2 border-2 border-indigo-400/30 overflow-hidden" data-id="element-17421">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20" data-id="element-17422"></div>
                        <div className="absolute inset-0 flex items-center justify-center" data-id="element-17423">
                          <div className="w-12 h-12 rounded-full bg-indigo-500/30 flex items-center justify-center" data-id="element-17424">
                            <Star size={24} className="text-amber-300" data-id="element-17425" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-0 right-0 text-center" data-id="element-17426">
                          <span className="text-xs text-amber-200 font-medium" data-id="element-17427">
                            Mystery Bonus
                          </span>
                        </div>
                      </div>
                      {/* Animated glow */}
                      <div className="absolute inset-0 bg-indigo-400/20 rounded-xl blur-sm animate-pulse-subtle" data-id="element-17428"></div>
                    </div>)}
                </div> : <div className="w-full max-w-sm mb-6" data-id="element-17429">
                  {/* Revealed bonus card */}
                  <div className="relative w-full bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl p-6 border-2 border-indigo-400/30 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500" data-id="element-17430">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-17431"></div>
                    {/* Bonus content */}
                    <div className="relative z-10" data-id="element-17432">
                      <div className="mb-4 flex justify-center" data-id="element-17433">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${revealedBonus?.color || 'from-amber-400 to-orange-400'} flex items-center justify-center`} data-id="element-17434">
                          {revealedBonus?.icon || <Star size={32} className="text-white" data-id="element-17435" />}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white text-center mb-2" data-id="element-17436">
                        {revealedBonus?.title || 'Mystery Bonus'}
                      </h3>
                      <p className="text-white/80 text-sm text-center mb-4" data-id="element-17437">
                        {revealedBonus?.description || 'You unlocked a special bonus reward!'}
                      </p>
                      {/* Animated sparkles */}
                      <div className="absolute inset-0 pointer-events-none" data-id="element-17438">
                        {sparkCount.slice(0, 6).map(i => <div key={`bonus-spark-${i}`} className="absolute w-1.5 h-1.5 bg-yellow-300 rounded-full animate-float-spark" style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${3 + Math.random() * 4}s`
                  }} data-id="element-17439"></div>)}
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Main reward display */}
              {selectedCard !== null && <div className="w-full bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-lg p-4 mb-4 border border-amber-500/30" data-id="element-17440">
                  <h4 className="font-medium text-amber-300 text-sm mb-3 text-center" data-id="element-17441">
                    Your Weekly Lootbox Reward
                  </h4>
                  <div className="flex items-center justify-center gap-4" data-id="element-17442">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${reward?.rarity === 'common' ? 'bg-gray-700' : reward?.rarity === 'rare' ? 'bg-blue-800' : reward?.rarity === 'epic' ? 'bg-purple-800' : 'bg-amber-800'}`} data-id="element-17443">
                      {reward?.type === 'tokens' && <span className="text-2xl" data-id="element-17444">💗</span>}
                      {reward?.type === 'gems' && <Diamond size={28} className="text-blue-300" data-id="element-17445" />}
                      {reward?.type === 'vip' && <Crown size={28} className="text-amber-300" data-id="element-17446" />}
                      {reward?.type === 'skin' && <Sparkles size={28} className="text-pink-300" data-id="element-17447" />}
                      {reward?.type === 'chatTime' && <Clock size={28} className="text-green-300" data-id="element-17448" />}
                      {reward?.type === 'picture' && <Image size={28} className="text-purple-300" data-id="element-17449" />}
                      {reward?.type === 'video' && <Play size={28} className="text-red-300" data-id="element-17450" />}
                    </div>
                    <div className="flex-1" data-id="element-17451">
                      <h3 className="text-lg font-bold text-white" data-id="element-17452">
                        {reward?.title}
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-17453">
                        <span className={`text-xs px-1.5 py-0.5 rounded-sm ${reward?.rarity === 'common' ? 'bg-gray-700 text-gray-300' : reward?.rarity === 'rare' ? 'bg-blue-800 text-blue-300' : reward?.rarity === 'epic' ? 'bg-purple-800 text-purple-300' : 'bg-amber-800 text-amber-300'}`} data-id="element-17454">
                          {reward?.rarity?.toUpperCase()}
                        </span>
                        <span className="text-white font-medium text-sm" data-id="element-17455">
                          {reward?.type === 'tokens' && `${reward.amount} LuvTokens`}
                          {reward?.type === 'gems' && `${reward.amount} Gems`}
                          {reward?.type === 'vip' && `${reward.amount} Days VIP`}
                          {reward?.type === 'chatTime' && `${reward.amount} Minutes`}
                          {reward?.type === 'skin' && `Exclusive Skin`}
                          {reward?.type === 'picture' && `Picture Set`}
                          {reward?.type === 'video' && `Premium Video`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Action buttons */}
              <div className="w-full flex flex-col gap-2" data-id="element-17456">
                {selectedCard !== null && showShareOption ? <>
                    <button className="w-full py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300 flex items-center justify-center gap-2" onClick={onClose} data-id="element-17457">
                      <ChevronUp size={16} data-id="element-17458" />
                      <span data-id="element-17459">Claim All Rewards</span>
                    </button>
                    <button className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 flex items-center justify-center gap-2" onClick={handleShareReward} data-id="element-17460">
                      <Share2 size={16} data-id="element-17461" />
                      <span data-id="element-17462">Share Your Rewards</span>
                    </button>
                  </> : selectedCard === null ? <div className="text-center text-white/60 text-sm" data-id="element-17463">
                    Choose a card to reveal your mystery bonus
                  </div> : <div className="w-16 h-16 mx-auto relative" data-id="element-17464">
                    <div className="w-full h-full rounded-full border-4 border-indigo-500/30 border-t-indigo-500 animate-spin" data-id="element-17465"></div>
                    <div className="absolute inset-0 flex items-center justify-center text-indigo-300" data-id="element-17466">
                      <Plus size={24} className="animate-pulse" data-id="element-17467" />
                    </div>
                  </div>}
              </div>
            </div>
          </div> : <div className="bg-gradient-to-b from-purple-900 to-black p-8 rounded-2xl shadow-2xl" data-id="element-17468">
            <div className="flex flex-col items-center" data-id="element-17469">
              <h2 className="text-white text-xl font-bold mb-2" data-id="element-17470">
                Reward Unlocked!
              </h2>
              <p className="text-amber-300 text-sm mb-6 text-center font-medium" data-id="element-17471">
                From your Premium Weekly Lootbox
              </p>
              <div className="relative w-full max-w-xs" data-id="element-17472">
                <div className={`w-full p-6 rounded-xl border-2 ${reward?.rarity === 'common' ? 'border-gray-400 bg-gray-800/80' : reward?.rarity === 'rare' ? 'border-blue-400 bg-blue-900/30' : reward?.rarity === 'epic' ? 'border-purple-400 bg-purple-900/30' : 'border-amber-400 bg-amber-900/30'} flex flex-col items-center animate-in zoom-in-95 duration-500`} data-id="element-17473">
                  <div className={`absolute -top-3 -right-3 px-2 py-1 rounded-full text-xs font-bold ${reward?.rarity === 'common' ? 'bg-gray-400 text-gray-900' : reward?.rarity === 'rare' ? 'bg-blue-400 text-blue-900' : reward?.rarity === 'epic' ? 'bg-purple-400 text-purple-900' : 'bg-amber-400 text-amber-900'}`} data-id="element-17474">
                    {reward?.rarity?.toUpperCase()}
                  </div>
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${reward?.rarity === 'common' ? 'bg-gray-700' : reward?.rarity === 'rare' ? 'bg-blue-800' : reward?.rarity === 'epic' ? 'bg-purple-800' : 'bg-amber-800'}`} data-id="element-17475">
                    {reward?.type === 'tokens' && <span className="text-3xl" data-id="element-17476">💗</span>}
                    {reward?.type === 'gems' && <Diamond size={36} className="text-blue-300" data-id="element-17477" />}
                    {reward?.type === 'vip' && <Crown size={36} className="text-amber-300" data-id="element-17478" />}
                    {reward?.type === 'skin' && <Sparkles size={36} className="text-pink-300" data-id="element-17479" />}
                    {reward?.type === 'chatTime' && <Clock size={36} className="text-green-300" data-id="element-17480" />}
                    {reward?.type === 'picture' && <Image size={36} className="text-purple-300" data-id="element-17481" />}
                    {reward?.type === 'video' && <Play size={36} className="text-red-300" data-id="element-17482" />}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1" data-id="element-17483">
                    {reward?.title}
                  </h3>
                  <p className="text-white/70 text-sm text-center mb-4" data-id="element-17484">
                    {reward?.description}
                  </p>
                  {reward?.imageUrl && <div className="w-full h-40 rounded-lg overflow-hidden mb-4" data-id="element-17485">
                      <img src={reward.imageUrl} alt={reward.title} className="w-full h-full object-cover" data-id="element-17486" />
                    </div>}
                  <div className="flex items-center justify-center gap-2" data-id="element-17487">
                    <Sparkles size={16} className="text-yellow-400 animate-pulse" data-id="element-17488" />
                    <span className="text-white font-bold" data-id="element-17489">
                      {reward?.type === 'tokens' && `${reward.amount} LuvTokens`}
                      {reward?.type === 'gems' && `${reward.amount} Gems`}
                      {reward?.type === 'vip' && `${reward.amount} Days VIP`}
                      {reward?.type === 'chatTime' && `${reward.amount} Minutes`}
                      {reward?.type === 'skin' && `Exclusive Skin`}
                      {reward?.type === 'picture' && `Picture Set`}
                      {reward?.type === 'video' && `Premium Video`}
                    </span>
                    <Sparkles size={16} className="text-yellow-400 animate-pulse" data-id="element-17490" />
                  </div>
                </div>
                {/* Particle effects around the reward */}
                <div className="absolute inset-0 pointer-events-none" data-id="element-17491">
                  <div className="absolute top-1/4 left-0 w-2 h-2 bg-pink-500 rounded-full animate-float-1" data-id="element-17492"></div>
                  <div className="absolute top-1/2 right-0 w-3 h-3 bg-purple-500 rounded-full animate-float-2" data-id="element-17493"></div>
                  <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-amber-500 rounded-full animate-float-3" data-id="element-17494"></div>
                  <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-blue-500 rounded-full animate-float-4" data-id="element-17495"></div>
                </div>
              </div>
              <button className="mt-6 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300 flex items-center justify-center gap-2" onClick={() => setShowMysteryCards(true)} data-id="element-17496">
                <Sparkles size={16} data-id="element-17497" />
                <span data-id="element-17498">Reveal Mystery Bonus</span>
                <Sparkles size={16} data-id="element-17499" />
              </button>
            </div>
          </div>}
      </div>
    </div>;
};